import React from 'react';
import { useSelector } from 'react-redux';
import { Form, Formik, useFormikContext } from 'formik';
import * as yup from 'yup';
import { deburr } from 'lodash';
import { Box, Button, Checkbox, DialogActions, DialogContent, Grid, TextField, Typography } from '@material-ui/core';
import { AutocompleteRenderOptionState, FilterOptionsState } from '@material-ui/lab';
import { Cancel, Done } from '@material-ui/icons';
import { RootState } from 'interfaces';
import { USER_OPERATIONS } from 'helper';
import { FileInput } from 'components/FileInput';
import AsyncSearch from 'components/AsyncSearch';
import { getAreas, getRealEstatesShort, getZones } from '../../api';
import { useCreateOrUpdateArticle } from '../../hooks';
import { Confluence, IArea, IRealEstate } from '../../interfaces';

const initialValues: Confluence.ILessonArticle = {
  name: '',
  template: {
    area: null,
    realEstates: [],
    zone: null,
    situation: '',
    reason: '',
    solution: '',
    recommendation: '',
    effect: '',
    photoBefore: [],
    photoAfter: [],
  },
};

const validationSchema = yup.object().shape({
  name: yup.string().required('Обязательное поле'),
  template: yup.object().shape({
    area: yup.object().nullable().required('Обязательное поле'),
    realEstates: yup
      .array()
      .of(yup.object().nullable().required('Обязательное поле'))
      .min(1, 'Выберите как минимум один объект недвижимости'),
    zone: yup.object().nullable().required('Обязательное поле'),
    situation: yup.string().required('Обязательное поле'),
    reason: yup.string().required('Обязательное поле'),
    solution: yup.string().required('Обязательное поле'),
    recommendation: yup.string(),
    effect: yup.string().required('Обязательное поле'),
    photoBefore: yup.array(),
    photoAfter: yup.array(),
  }),
});

interface IProps {
  article?: Confluence.ILessonArticle | null;
  parentId?: number | null;
  onClose(): void;
  onCreateOrUpdate?(article: Confluence.IArticleBase): void;
}

const LessonTemplate = ({ article, parentId, onClose, onCreateOrUpdate }: IProps) => {
  const operations = useSelector((state: RootState) => state.operations);

  const { loading, handleCreateOrUpdateLessonArticle } = useCreateOrUpdateArticle(article, parentId, (articleData) => {
    onClose();
    if (onCreateOrUpdate) onCreateOrUpdate(articleData);
  });

  // Methods
  function setInitialValues(article: Confluence.ILessonArticle): Confluence.ILessonArticle {
    const { areaId, areaName, zoneId, zoneName, realEstates, ...template } = article.template;

    return {
      ...article,
      template: {
        ...template,
        area: { areaId, name: areaName },
        realEstates: (realEstates || []).map((realEstate) => ({
          guid: realEstate.realEstateGuid,
          name: realEstate.realEstateName,
        })),
        zone: { zoneId, zoneName },
      },
    };
  }

  // Main render
  return (
    <Formik
      onSubmit={handleCreateOrUpdateLessonArticle}
      validationSchema={validationSchema}
      initialValues={article ? setInitialValues(article) : initialValues}
      enableReinitialize
    >
      <Form>
        <DialogContent dividers style={{ overflow: 'visible' }}>
          <LessonTemplateForm article={article} loading={loading} />
        </DialogContent>

        <DialogActions>
          <Button variant="contained" disabled={loading} onClick={onClose} startIcon={<Cancel />}>
            Отменить
          </Button>

          {operations.includes(USER_OPERATIONS.CONFLUENCE_LESSON_MANAGER) && (
            <Button color="primary" variant="contained" disabled={loading} type="submit" startIcon={<Done />}>
              {article ? 'Обновить' : 'Добавить'}
            </Button>
          )}
        </DialogActions>
      </Form>
    </Formik>
  );
};

interface ILessonTemplateForm {
  article?: Confluence.ILessonArticle | null;
  loading: boolean;
}

const LessonTemplateForm = ({ loading }: ILessonTemplateForm) => {
  const { values, touched, errors, setFieldValue, handleChange } = useFormikContext<Confluence.ILessonArticle>();

  // Methods
  const renderOption = (realEstate: IRealEstate, state: AutocompleteRenderOptionState) => (
    <Box sx={{ display: 'flex', gridGap: 8 }}>
      <Checkbox checked={state.selected} color="primary" />

      <Box>
        <Typography style={{ margin: 0 }}>{realEstate.name}</Typography>

        {realEstate.addressRu && realEstate.addressRu.length > 0 && (
          <Typography style={{ margin: 0, color: ' rgba(0, 0, 0, 0.5)' }}>
            <small>{realEstate.addressRu}</small>
          </Typography>
        )}
      </Box>
    </Box>
  );

  const filterOptions = (options: IRealEstate[], { inputValue }: FilterOptionsState<IRealEstate>): IRealEstate[] => {
    const searchValue: string = inputValue === null ? '' : deburr(inputValue.trim()).toLowerCase();

    return options.filter(
      (option) =>
        // FIXME: addressRu - is a temp field. Need to replace to address
        option?.name!.toLowerCase().indexOf(searchValue) > -1 ||
        option?.addressRu!.toLowerCase().indexOf(searchValue) > -1,
    );
  };

  function handleChangeArea(area: IArea | null) {
    setFieldValue('template.area', area);
    setFieldValue('template.realEstates', []);
  }

  // Main render
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          name="name"
          value={values.name}
          variant="filled"
          label="Заголовок статьи"
          placeholder="Введите значение"
          fullWidth
          disabled={loading}
          onChange={handleChange}
          error={Boolean(touched.name && errors.name)}
          helperText={touched.name && errors.name}
        />
      </Grid>

      <Grid item lg={6} md={6} xs={12}>
        <AsyncSearch
          value={values.template.area}
          fetch={getAreas}
          uniqueBy="areaId"
          matchKeys={['name']}
          onChange={(_, option) => handleChangeArea(option)}
          disabled={loading}
          getOptionLabel={(option) => String(option.name)}
          getOptionSelected={(option, value) => option.areaId === value.areaId}
          renderInput={(params) => (
            <TextField
              {...params}
              name="template.area"
              variant="filled"
              label="Участок"
              placeholder="Выберите из списка"
              fullWidth
              error={Boolean(touched.template?.area && errors.template?.area)}
              helperText={touched.template?.area && errors.template?.area}
            />
          )}
        />
      </Grid>

      <Grid item lg={6} md={6} xs={12}>
        <AsyncSearch
          value={values.template.realEstates}
          multiple
          fetch={() =>
            getRealEstatesShort({ areaIds: [values.template.area?.areaId!], nameWithCity: true, searchWithCity: true })
          }
          uniqueBy="guid"
          matchKeys={['name', 'addressRu']}
          config={{ params: { areaId: values.template.area?.areaId } }}
          onChange={(_, option) => setFieldValue('template.realEstates', option)}
          disabled={loading || !values.template.area}
          getOptionLabel={(option) => String(option.name || option.realEstateName)}
          renderOption={renderOption}
          filterOptions={filterOptions}
          disableCloseOnSelect
          loadingText="Загрузка..."
          getOptionSelected={(option, value) =>
            (option.guid || option.realEstateGuid) === (value.guid || value.realEstateGuid)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              name="template.realEstates"
              variant="filled"
              label="Список объектов недвижимости"
              placeholder="Выберите из списка"
              fullWidth
              error={Boolean(touched.template?.realEstates && errors.template?.realEstates)}
              helperText={touched.template?.realEstates && errors.template?.realEstates}
            />
          )}
        />
      </Grid>

      <Grid item lg={6} md={6} xs={12}>
        <AsyncSearch
          value={values.template.zone}
          fetch={getZones}
          uniqueBy="zoneId"
          matchKeys={['zoneName']}
          getOptionLabel={(option) => String(option.zoneName)}
          onChange={(_, option) => setFieldValue('template.zone', option)}
          disabled={loading}
          getOptionSelected={(option, value) => option.zoneId === value.zoneId}
          renderInput={(params) => (
            <TextField
              {...params}
              name="zone"
              variant="filled"
              label="Зона"
              placeholder="Выберите из списка или введите вручную"
              fullWidth
              error={Boolean(touched.template?.zone && errors.template?.zone)}
              helperText={touched.template?.zone && errors.template?.zone}
            />
          )}
        />
      </Grid>

      <Grid item lg={6} md={6} xs={12}>
        <TextField
          name="template.situation"
          value={values.template.situation}
          variant="filled"
          label="Ситуация"
          placeholder="Введите значение"
          fullWidth
          disabled={loading}
          onChange={handleChange}
          error={Boolean(touched.template?.situation && errors.template?.situation)}
          helperText={touched.template?.situation && errors.template?.situation}
        />
      </Grid>

      <Grid item lg={6} md={6} xs={12}>
        <TextField
          name="template.reason"
          value={values.template.reason}
          variant="filled"
          label="Причины"
          placeholder="Введите значение"
          fullWidth
          disabled={loading}
          onChange={handleChange}
          error={Boolean(touched.template?.reason && errors.template?.reason)}
          helperText={touched.template?.reason && errors.template?.reason}
        />
      </Grid>

      <Grid item lg={6} md={6} xs={12}>
        <TextField
          name="template.solution"
          value={values.template.solution}
          variant="filled"
          label="Решение"
          placeholder="Введите значение"
          fullWidth
          disabled={loading}
          onChange={handleChange}
          error={Boolean(touched.template?.solution && errors.template?.solution)}
          helperText={touched.template?.solution && errors.template?.solution}
        />
      </Grid>

      <Grid item lg={6} md={6} xs={12}>
        <TextField
          name="template.recommendation"
          value={values.template.recommendation}
          variant="filled"
          label="Рекомендации"
          placeholder="Введите значение"
          fullWidth
          disabled={loading}
          onChange={handleChange}
          error={Boolean(touched.template?.recommendation && errors.template?.recommendation)}
          helperText={touched.template?.recommendation && errors.template?.recommendation}
        />
      </Grid>

      <Grid item lg={6} md={6} xs={12}>
        <TextField
          name="template.effect"
          value={values.template.effect}
          variant="filled"
          label="Экономический эффект/финансовые потери"
          placeholder="Введите значение"
          fullWidth
          disabled={loading}
          onChange={handleChange}
          error={Boolean(touched.template?.effect && errors.template?.effect)}
          helperText={touched.template?.effect && errors.template?.effect}
        />
      </Grid>

      <Grid item lg={6} md={6} xs={12}>
        <FileInput
          accept=".jpeg, .png, .jpg, .svg, .tiff, .gif"
          multiple
          value={values.template.photoBefore}
          onChange={(files) => setFieldValue('template.photoBefore', files)}
          helperText={touched.template?.photoBefore ? errors.template?.photoBefore : ''}
          FormControlProps={{
            error: Boolean(touched.template?.photoBefore && errors.template?.photoBefore),
          }}
        />
      </Grid>

      <Grid item lg={6} md={6} xs={12}>
        <FileInput
          accept=".jpeg, .png, .jpg, .svg, .tiff, .gif"
          multiple
          value={values.template.photoAfter}
          onChange={(files) => setFieldValue('template.photoAfter', files)}
          helperText={touched.template?.photoAfter ? errors.template?.photoAfter : ''}
          FormControlProps={{
            error: Boolean(touched.template?.photoAfter && errors.template?.photoAfter),
          }}
        />
      </Grid>
    </Grid>
  );
};

export default LessonTemplate;
